import { useState } from 'react';
import { AlertTriangle, ArrowRight, BarChart3, Check, CheckCircle2, ChevronDown, ChevronUp, Clock, Cloud, CloudRain, Pencil, Sun, Upload, X } from 'lucide-react';
import { Meeting, useSetMeetingVisibility } from '../../queries/meetings';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { RaceMatrixView } from './RaceDrillDown/RaceMatrixView';
import { MeetingModelSlots } from './MeetingModelSlots';
import { MeetingNoteEditor } from './MeetingNoteEditor';
import { TrackConditionsDialog } from './TrackConditionsDialog';
import { MeetingRacesDrawer } from './MeetingRacesDrawer';
import { UploadOddsDialog } from './UploadOddsDialog';
import {
  formatConditionLabel,
  formatDateLocal,
  getConditionDrift,
  getConditionStyle,
  getMeetingReadiness,
  getStatusBadge,
  getWeatherIconKind,
} from './meetings-helpers';

const WEATHER_ICONS = { rain: CloudRain, cloud: Cloud, sun: Sun };

function WeatherIcon({ weather }: { weather: string | null }) {
  const Icon = WEATHER_ICONS[getWeatherIconKind(weather)];
  return <Icon className="h-3.5 w-3.5" />;
}

function DataCheck({ label, ok, partial }: { label: string; ok: boolean; partial?: string }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-muted-foreground">{label}:</span>
      {ok ? (
        <Check className="h-3.5 w-3.5 text-emerald-500" />
      ) : partial ? (
        <span className="font-medium text-amber-600">{partial}</span>
      ) : (
        <X className="h-3.5 w-3.5 text-red-500" />
      )}
    </div>
  );
}

const NOTE_BG: Record<string, string> = {
  danger: 'bg-red-50 text-red-800 dark:bg-red-950/20 dark:text-red-300',
  warning: 'bg-orange-50 text-orange-800 dark:bg-orange-950/20 dark:text-orange-300',
  info: 'bg-blue-50 text-blue-800 dark:bg-blue-950/20 dark:text-blue-300',
};

export function MeetingCard({ meeting }: { meeting: Meeting }) {
  const readiness = getMeetingReadiness(meeting);
  const status = getStatusBadge(meeting.status);
  const dc = meeting.data_completeness;
  const tc = meeting.track_condition_details;
  const conditionLabel = formatConditionLabel(tc);
  const drift = getConditionDrift(meeting);
  const visibility = useSetMeetingVisibility();
  const { success, error } = useToast();
  const [showMatrix, setShowMatrix] = useState(false);
  const [editingConditions, setEditingConditions] = useState(false);
  const [showRaces, setShowRaces] = useState(false);
  const [uploadingOdds, setUploadingOdds] = useState(false);

  const applyVisibility = (patch: { isPrivate?: boolean; isTraining?: boolean; platinumiqPrivate?: boolean }, label: string, on: boolean) =>
    visibility.mutate(
      { meetingId: meeting.meeting_id, ...patch },
      {
        onSuccess: () => success(`${label} ${on ? 'enabled' : 'disabled'} for ${meeting.track.name}.`),
        onError: () => error(`Couldn't update ${meeting.track.name}. Please try again.`),
      },
    );
  const setPrivate = (checked: boolean) => applyVisibility({ isPrivate: checked }, 'Private', checked);
  const setTraining = (checked: boolean) => applyVisibility({ isTraining: checked }, 'Training', checked);
  const setPlatinumPrivate = (checked: boolean) => applyVisibility({ platinumiqPrivate: checked }, 'PlatinumIQ Private', checked);

  return (
    <div className="@container rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold">{meeting.track.name}</h3>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.className}`}>{status.label}</span>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
            readiness.ready
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
              : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
          }`}
        >
          {readiness.ready ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
          {readiness.label}
        </span>
        {tc?.abandoned && (
          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-400">Abandoned</span>
        )}
      </div>

      <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
        <span>{meeting.track.state}</span>
        <span>&middot;</span>
        <span>{meeting.track.type?.toLowerCase()}</span>
        <span>&middot;</span>
        <span>{meeting.race_count ?? '—'} races</span>
      </p>

      <MeetingModelSlots
        meetingId={meeting.meeting_id}
        hasAnalysis={(meeting.analysis_status?.normal_analysis_races ?? 0) > 0}
      />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {conditionLabel ? (
          <button
            type="button"
            onClick={() => setEditingConditions(true)}
            title="Edit track conditions"
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-shadow hover:ring-2 hover:ring-primary/40 ${getConditionStyle(
              tc?.track_condition ?? null
            )}`}
          >
            <WeatherIcon weather={tc?.weather ?? null} />
            {conditionLabel}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setEditingConditions(true)}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            <Pencil className="h-3.5 w-3.5" />
            Add conditions
          </button>
        )}
        {drift && (
          <span
            title={drift.alert ? 'The track has gone the wrong way since the tips were generated' : 'The going differs from the prediction'}
            className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium ${drift.className}`}
          >
            {drift.alert && <AlertTriangle className="h-3.5 w-3.5" />}
            {drift.label}
          </span>
        )}
      </div>
      {editingConditions && (
        <TrackConditionsDialog meetingId={meeting.meeting_id} trackName={meeting.track.name} onClose={() => setEditingConditions(false)} />
      )}

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-3 text-sm">
        <DataCheck
          label="Odds"
          ok={!!dc?.has_odds && dc.races_with_odds === dc.total_races}
          partial={dc?.has_odds && dc.races_with_odds !== dc.total_races ? `${dc.races_with_odds}/${dc.total_races}` : undefined}
        />
        <DataCheck label="Form" ok={!!dc?.has_form_data} />
        <DataCheck label="Conditions" ok={!!dc?.has_track_conditions} />
      </div>

      {meeting.meeting_note && (
        <div className={`mt-2 rounded-lg px-3 py-2 text-sm ${NOTE_BG[meeting.meeting_note_type ?? 'info']}`}>
          {meeting.meeting_note}
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 gap-4 border-t border-border pt-3 @sm:grid-cols-2">
        <div className="space-y-2.5">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={meeting.is_private} disabled={visibility.isPending} onChange={(e) => setPrivate(e.target.checked)} />
            Private Meeting
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={meeting.is_training} disabled={visibility.isPending} onChange={(e) => setTraining(e.target.checked)} />
            Training Meeting
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={meeting.platinumiq_private}
              disabled={visibility.isPending}
              onChange={(e) => setPlatinumPrivate(e.target.checked)}
            />
            PlatinumIQ Private
          </label>
        </div>
        <div className="flex flex-col items-start gap-2 @sm:items-end">
          <Button size="sm" onClick={() => setShowRaces(true)}>
            Open races
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setUploadingOdds(true)}>
            <Upload className="h-4 w-4" />
            Upload odds
          </Button>
        </div>
      </div>
      {uploadingOdds && <UploadOddsDialog meetingId={meeting.meeting_id} trackName={meeting.track.name} onClose={() => setUploadingOdds(false)} />}
      {showRaces && (
        <MeetingRacesDrawer
          meetingId={meeting.meeting_id}
          trackName={meeting.track.name}
          meetingDate={formatDateLocal(new Date(meeting.meeting_date))}
          onClose={() => setShowRaces(false)}
        />
      )}

      <div className="mt-3 border-t border-border pt-3">
        <button
          type="button"
          onClick={() => setShowMatrix((v) => !v)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          <BarChart3 className="h-3.5 w-3.5" />
          {showMatrix ? 'Hide race matrix' : 'Show race matrix'}
          {showMatrix ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        {/* Lazily fetched the first time a card is expanded (useRaceModelMatrix
            caches thereafter), matching the old admin's per-tile matrix preview
            so staff can scan analysis coverage without leaving the list. */}
        {showMatrix && (
          <div className="mt-3 overflow-x-auto">
            <RaceMatrixView meetingId={meeting.meeting_id} />
          </div>
        )}
      </div>

      <div className="mt-3 space-y-2 border-t border-border pt-3">
        <MeetingNoteEditor meetingId={meeting.meeting_id} variant="operator" note={meeting.meeting_note} noteType={meeting.meeting_note_type} />
        <MeetingNoteEditor meetingId={meeting.meeting_id} variant="admin" note={meeting.notes} />
      </div>
    </div>
  );
}
